import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {DataType, FormPage, FormType, InputField} from '@models/sidebar/response/form-page';
import {PageItem} from '@models/sidebar/page-item';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';
import {GlobalHelperService} from '@services/global-helper/global-helper.service';

@Injectable({
  providedIn: 'root'
})
export class FormPageService {

  private GET_FORM_PAGE_URL_SUFFIX = '/api/web/base/:machineId/formPage/:pageId';
  private CREATE_FORM_PAGE_URL_SUFFIX = '/api/web/base/:machineId/formPage';
  private DELETE_FORM_PAGE_URL_SUFFIX = '/api/web/base/:machineId/formPage/:pageId/baseId/:itemId';
  private RENAME_FORM_PAGE_URL_SUFFIX = '/api/web/base/:machineId/formPage/rename/baseId/:itemId';
  private SAVE_FORM_PAGE_URL_SUFFIX = '/api/web/base/:machineId/formPage/save';
  private SUBMIT_FORM_URL_SUFFIX = '/api/web/base/:machineId/formPage/submit';
  private LAST_SUBMITTED_ROW_URL_SUFFIX = '/api/web/base/:machineId/formPage/lastRow/:pageId';
  private VALIDATE_TABLE_NAME_URL_SUFFIX = '/api/web/base/:machineId/formPage/validate/tableName/:pageId/:tableName';

  private readonly savingIndicatorMessage = '';
  private readonly errorCreatingPage = '';
  private readonly successCreatingPage = '';
  private readonly errorRenamingPage = '';
  private readonly successRenamingPage = '';

  private readonly BASE_URL: string;
  private MAX_COLUMN_NAME_LENGTH_BQ: number = 300;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private leftMenuService: LeftMenuService,
              private notificationService: NotificationService,
              private menuNotificationService: MenuNotificationService,
              private translateService: TranslateService,
              private globalHelper: GlobalHelperService,
  ) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.savingIndicatorMessage = this.translateService.instant('Saving');
    this.errorCreatingPage = this.translateService.instant('Error creating form page.');
    this.successCreatingPage = this.translateService.instant('Form page was created.');
    this.errorRenamingPage = this.translateService.instant('Error renaming form page.');
    this.successRenamingPage = this.translateService.instant('Form page was renamed.');
  }

  public getFormPage(machineId: string, pageId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_FORM_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId).replace(':pageId', pageId);

    return this.http.get<FormPage>(url, HTTPS_REQUEST_OPTIONS);
  }

  public create(baseMenuItems: PageItem[], pageItem: PageItem, machineId: string | null, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean): void {
    if (machineId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const formPageTab = this.createBasicFormPage(pageItem.name);
      this.createFormPage(machineId, formPageTab).subscribe((newBaseMenuItem: BaseMenuItem) => {
        this.handleCreatedPage(newBaseMenuItem, pageItem, baseMenuItems, currentPageName, parentId, isAdminOrHigher);
      }, (error) => {
        this.notificationService.error(this.errorCreatingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  public save(machineId: string, request: FormPage): Observable<any> {
    let url: string = this.BASE_URL + this.SAVE_FORM_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.post<FormPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public submit(machineId: string, request: FormPage): Observable<any> {
    let url: string = this.BASE_URL + this.SUBMIT_FORM_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.post<FormPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public getLastSubmittedRow(machineId: string, pageId: number): Observable<any> {
    let url: string = this.BASE_URL + this.LAST_SUBMITTED_ROW_URL_SUFFIX;
    url = url.replace(':machineId', machineId).replace(':pageId', String(pageId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public deleteLastSubmittedRow(machineId: string, pageId: number, timestamp: number): Observable<any> {
    let url: string = this.BASE_URL + this.LAST_SUBMITTED_ROW_URL_SUFFIX;
    url = url.replace(':machineId', machineId).replace(':pageId', String(pageId));
    return this.http.post(url, timestamp, HTTPS_REQUEST_OPTIONS);
  }

  public validateTableName(machineId: string, pageId: number, tableName: string): Observable<any> {
    let url: string = this.BASE_URL + this.VALIDATE_TABLE_NAME_URL_SUFFIX;
    url = url.replace(':machineId', machineId).replace(':pageId', String(pageId)).replace(':tableName', tableName);
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public getInputType(inputField: InputField): string {
    switch (inputField.datatype) {
      case DataType.STRING:
        return 'text';
      case DataType.FLOAT:
      case DataType.INTEGER:
        return 'number';
      case DataType.BOOLEAN:
        return 'checkbox';
      case DataType.TIMESTAMP:
        return 'date';
      default:
        return '';
    }
  }

  public bigQueryCompatibleName(inputString: string): boolean {
    if (!inputString || inputString.isNullOrWhitespace()) {
      return false;
    }

    if (!(/^[0-9a-zA-Z_]+$/).test(inputString)) {
      return false;
    }

    if (!(/^[A-Za-z]/).test(inputString) && inputString.charAt(0) != '_') {
      return false;
    }

    if (inputString.length > this.MAX_COLUMN_NAME_LENGTH_BQ) {
      return false;
    }

    return true;
  }

  public deleteFormPage(machineId: string, pageId: string, itemId: string): Observable<any> {
    let url: string = this.BASE_URL + this.DELETE_FORM_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':pageId', pageId)
    .replace(':itemId', itemId);
    return this.http.delete<FormPage>(url, HTTPS_REQUEST_OPTIONS);
  }

  public rename(pageItem: PageItem, machineId: string | null, parentId: string | null, currentPageName: string, nameFallback: Object): void {
    if (machineId && pageItem.id && pageItem.pageId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const formPage = this.createBasicFormPage(pageItem.name);
      formPage.id = Number(pageItem.pageId);
      const itemId: string = pageItem.id;
      this.renameFormPage(machineId, itemId, formPage).subscribe((result: BaseMenuItem) => {
        this.globalHelper.handleUpdatedPage(result.name, parentId, currentPageName, itemId, this.successRenamingPage);
      }, (error) => {
        this.leftMenuService.broadcastErrorUpdatePageChange(nameFallback);
        this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  private createFormPage(machineId: string, request: FormPage): Observable<any> {
    let url: string = this.BASE_URL + this.CREATE_FORM_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.post<FormPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private handleCreatedPage(createdItem: BaseMenuItem, pageItem: PageItem, baseMenuItems: PageItem[], currentPageName: string, parentId: string, isAdminOrHigher: boolean) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, createdItem.name, ''));
    pageItem.id = createdItem.id;
    pageItem.canBeAltered = isAdminOrHigher;
    pageItem.parent = createdItem.parent;
    pageItem.pageId = createdItem.pageId;
    pageItem.index = createdItem.index;
    baseMenuItems.push(pageItem);
    this.loadingIndicator.hide();
    this.notificationService.success(this.successCreatingPage, currentPageName, false);
  }

  private renameFormPage(machineId: string, itemId: string, request: FormPage): Observable<any> {
    let url: string = this.BASE_URL + this.RENAME_FORM_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId).replace(':itemId', itemId);
    return this.http.post<FormPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private createBasicFormPage(label: string): FormPage {
    const formPageTab = new FormPage();
    formPageTab.label = label;
    formPageTab.type = FormType.BASIC;
    return formPageTab;
  }
}
